import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import Health from '@/assets/icons/health.svg?component';
import Shield from '@/assets/icons/shield.svg?component';
import ContentBlock from '@/components/molecules/ContentBlock';
import type { SmoltWelfareData } from '@/domain/smoltReport';
import { formatPercentageByLocale, parseLocale } from '@/utils/localeFormatters';

import BehaviorMetricCard from '../../../CageBehavior/BehaviorDashboard/components/BehaviorMetricCard/BehaviorMetricCard';
import GridLayout from '../../shared/GridLayout';
import SectionHeader from '../../shared/SectionHeader';

import { LaksvelParameterCard, WoundsCard } from './WelfareCards';

import styles from './WelfareSection.module.scss';

interface WelfareSectionProps {
    isLoading?: boolean;
    welfareData: SmoltWelfareData;
    isPdfExport?: boolean;
    /** Shown instead of the section's figures when the report holds none to show. */
    unavailableNotice?: string;
}

/**
 * The welfare parameters the report was created with: the wound figures for body wounds, and a
 * card per Laksvel score parameter. A report created with none checked has no welfare section.
 */
export default function WelfareSection(props: WelfareSectionProps) {
    const { welfareData, isLoading = false, isPdfExport = false, unavailableNotice } = props;

    const { t, i18n } = useTranslation();
    const locale = parseLocale(i18n.language);

    // Nothing chosen means no welfare section, with or without figures to say about it.
    if (welfareData.includedParameters.length === 0) {
        return null;
    }

    if (unavailableNotice) {
        return (
            <div>
                <SectionHeader
                    title={t('smoltReport.detailView.welfareTitle')}
                    subtitle={t('smoltReport.detailView.welfareSubtitle')}
                />
                <p className={styles.unavailableNotice}>{unavailableNotice}</p>
            </div>
        );
    }

    const { totalWounds, fishWithoutWounds, wounds, laksvelParameters } = welfareData;
    const totalWoundsPercentage = totalWounds != null ? totalWounds * 100 : null;
    const fishWithoutWoundsPercentage = fishWithoutWounds != null ? fishWithoutWounds * 100 : null;
    const gridClassName = classNames(styles.metricsGridSpacing, {
        [styles.metricsGrid]: !isPdfExport,
    });

    return (
        <div>
            <SectionHeader
                title={t('smoltReport.detailView.welfareTitle')}
                subtitle={t('smoltReport.detailView.welfareSubtitle')}
            />
            {wounds && (
                <GridLayout
                    keepCellsTogetherInPdf
                    contentLayout="2"
                    responsive={false}
                    className={gridClassName}>
                    <ContentBlock spacing="none">
                        <BehaviorMetricCard
                            title={t('smoltReport.detailView.withoutWoundsTitle')}
                            value={formatPercentageByLocale(fishWithoutWoundsPercentage, locale, 2)}
                            isLoading={isLoading}
                            icon={Shield}
                            tone="success"
                            iconCustomSize={24}
                        />
                    </ContentBlock>
                    <ContentBlock spacing="none">
                        <BehaviorMetricCard
                            title={t('smoltReport.detailView.totalWoundsTitle')}
                            value={formatPercentageByLocale(totalWoundsPercentage, locale, 2)}
                            isLoading={isLoading}
                            icon={Health}
                            tone="danger"
                            iconCustomSize={24}
                        />
                    </ContentBlock>
                </GridLayout>
            )}
            {(wounds || laksvelParameters.length > 0) && (
                <GridLayout
                    keepCellsTogetherInPdf
                    contentLayout="2"
                    responsive={false}
                    className={gridClassName}>
                    {wounds && (
                        <WoundsCard
                            wounds={wounds}
                            isLoading={isLoading}
                            isPdfExport={isPdfExport}
                        />
                    )}
                    {laksvelParameters.map((entry) => (
                        <LaksvelParameterCard
                            key={entry.parameter}
                            entry={entry}
                            isLoading={isLoading}
                            isPdfExport={isPdfExport}
                        />
                    ))}
                </GridLayout>
            )}
        </div>
    );
}
